import { computed, DestroyRef, inject, Signal, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import {
  AppRoutes,
  LOADING_TOKEN,
  LoadingProvider,
  MODAL_TOKEN,
  ModalProvider,
  TOAST_TOKEN,
  ToastProvider
} from '@core';
import { EMPTY, filter, Observable, switchMap, take, tap } from 'rxjs';
import { ClientEvent } from 'stream-chat-angular';
import { ChatChannelType } from '@core-http';
import { ChatInitializer } from '../../../application/initializers';
import {
  ChannelDetailsQueryHandler,
  ChannelDetailsQueryResult,
  CreateSingleChatCommandHandler
} from '../../../application/handlers';
import { ActiveChannelModel } from '../../../application/interfaces';
import { MessageContextStorage } from '../../../application/context-storage';
import { ManageChannelComponent } from '../../manage-channel/manage-channel.component';
import { STREAM_CHAT_CLIENT_TOKEN, StreamChatClientProvider } from '../../../infrastructure/tokens';
import { PENDING_DISPLAY_NAME_QUERY_PARAM, PENDING_USER_ID_QUERY_PARAM } from '../../../application/constants';
import { StreamChatEvent } from '../../../application/enums';

export class ChatViewService {
  private readonly loadingProvider: LoadingProvider = inject(LOADING_TOKEN);
  private readonly modalProvider: ModalProvider = inject(MODAL_TOKEN);
  private readonly chatInitializer: ChatInitializer = inject(ChatInitializer);
  private readonly channelDetailsQueryHandler: ChannelDetailsQueryHandler = inject(ChannelDetailsQueryHandler);
  private readonly createSingleChatCommandHandler: CreateSingleChatCommandHandler =
    inject(CreateSingleChatCommandHandler);
  private readonly messageContextStorage: MessageContextStorage = inject(MessageContextStorage);
  private readonly chatClientProvider: StreamChatClientProvider = inject(STREAM_CHAT_CLIENT_TOKEN);
  private readonly toastProvider: ToastProvider = inject(TOAST_TOKEN);
  private readonly router: Router = inject(Router);
  private readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  private readonly isPageLoadedSignal: WritableSignal<boolean> = signal<boolean>(false);
  private readonly channelDetailsSignal: WritableSignal<ChannelDetailsQueryResult | undefined> = signal<
    ChannelDetailsQueryResult | undefined
  >(undefined);
  private readonly pendingUserIdSignal: WritableSignal<string | null> = signal<string | null>(null);

  readonly isPageLoaded: Signal<boolean> = this.isPageLoadedSignal.asReadonly();
  readonly channelDetails: Signal<ChannelDetailsQueryResult | undefined> = this.channelDetailsSignal.asReadonly();
  readonly isPending: Signal<boolean> = computed(() => !!this.pendingUserIdSignal());

  constructor() {
    this.initialize();
  }

  manageChannel(): void {
    const details: ChannelDetailsQueryResult | undefined = this.channelDetailsSignal();
    if (!details || this.isPending()) return;

    this.modalProvider
      .showModal$({
        component: ManageChannelComponent,
        componentProps: {
          channelDetails: details,
          onMutedChange: (isMuted: boolean): void => this.setMuted(isMuted)
        },
        cssClass: 'modal-auto',
        initialBreakpoint: 1,
        breakpoints: [0, 1]
      })
      .subscribe();
  }

  stopWatching(): void {
    this.isPageLoadedSignal.set(false);
    this.channelDetailsSignal.set(undefined);
    this.pendingUserIdSignal.set(null);
    this.chatInitializer.stopWatching();
    this.messageContextStorage.clearAll();
  }

  private initialize(): void {
    const queryParamMap: ParamMap = this.activatedRoute.snapshot.queryParamMap;
    const pendingUserId: string | null = queryParamMap.get(PENDING_USER_ID_QUERY_PARAM);
    const pendingDisplayName: string | null = queryParamMap.get(PENDING_DISPLAY_NAME_QUERY_PARAM);

    if (pendingUserId) {
      this.enterPendingMode(pendingUserId, pendingDisplayName);
    } else {
      this.loadChannelDetails();
    }

    this.subscribeToChannelRemoval();
  }

  private loadChannelDetails(): void {
    this.loadingProvider
      .showLoading$()
      .pipe(
        switchMap(() => this.chatInitializer.activeChannelData$),
        filter((data: ActiveChannelModel): data is ActiveChannelModel => !!data?.channelOrlikfyId),
        take(1),
        switchMap((data: ActiveChannelModel) => this.channelDetailsQueryHandler.details(data.channelOrlikfyId)),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((details: ChannelDetailsQueryResult) => {
        this.channelDetailsSignal.set(details);
        this.isPageLoadedSignal.set(true);
        this.loadingProvider.dismissLoading();
      });
  }

  private setMuted(isMuted: boolean): void {
    this.channelDetailsSignal.update((details: ChannelDetailsQueryResult | undefined) =>
      details ? { ...details, isMuted } : details
    );
  }

  private subscribeToChannelRemoval(): void {
    this.chatClientProvider.events$
      .pipe(
        filter((event: ClientEvent) => event.eventType === StreamChatEvent.NotificationRemovedFromChannel),
        filter((event: ClientEvent) => event.event.channel_id === this.channelDetailsSignal()?.channelExternalId),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.toastProvider.showToast({
          message: 'Zostałeś usunięty z czatu'
        });

        this.stopWatching();
        this.router.navigateByUrl(AppRoutes.MESSAGES);
      });
  }

  promotePendingToReal(): Observable<void> {
    const pendingUserId: string | null = this.pendingUserIdSignal();
    if (!pendingUserId) return EMPTY;

    return this.createSingleChatCommandHandler.create(pendingUserId).pipe(
      take(1),
      tap(() => {
        this.pendingUserIdSignal.set(null);
        this.loadChannelDetails();
      })
    );
  }

  private enterPendingMode(pendingUserId: string, displayName: string | null): void {
    this.pendingUserIdSignal.set(pendingUserId);
    this.channelDetailsSignal.set(this.buildPendingChannelDetailsSurrogate(pendingUserId, displayName));
    this.isPageLoadedSignal.set(true);
  }

  private buildPendingChannelDetailsSurrogate(
    pendingUserId: string,
    displayName: string | null
  ): ChannelDetailsQueryResult {
    return {
      channelId: '',
      channelExternalId: '',
      channelName: displayName ?? 'Nowa wiadomość',
      channelThumbnail: null,
      channelType: ChatChannelType.single,
      closestGame: null,
      allGames: [],
      canEdit: false,
      isMuted: false,
      teamId: null,
      secondUserId: pendingUserId
    };
  }
}
